import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

type DecimalString = string | null;

export interface PositionMatchingRow {
  asset: string | null;
  internalLongMt: DecimalString;
  internalShortMt: DecimalString;
  internalNetMt: DecimalString;
  brokerLongMt: DecimalString;
  brokerShortMt: DecimalString;
  brokerNetMt: DecimalString;
  varianceMt: DecimalString;
  internalPriceFixNetMt: DecimalString;
  nMatched: number;
  nMismatch: number;
  nBrokerOnly: number;
  nInternalOnlyInWindow: number;
  nInternalOnlyPreWindow: number;
  nClosed: number;
  // 'all' — deliberate all-accounts comparison; present only while the LHA flag is on.
  accountScope?: string | null;
}

export class HedgingPositionMatchingApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingPositionMatchingApiError';
  }
}

async function fetchPositionMatching(): Promise<PositionMatchingRow[]> {
  // The backend endpoint still uses the original reconciliation route name;
  // the frontend product surface is Position Matching.
  const response = await authFetch(`${getApiBaseUrl()}/hedging/reconciliation`);
  if (!response.ok) {
    throw new HedgingPositionMatchingApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as PositionMatchingRow[];
}

export const HEDGING_POSITION_MATCHING_QUERY_KEY = ['hedging', 'position-matching'] as const;

export function useHedgingPositionMatching() {
  return useQuery({
    queryKey: HEDGING_POSITION_MATCHING_QUERY_KEY,
    queryFn: fetchPositionMatching,
  });
}
